import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Lock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCampusAuth } from "@/contexts/CampusAuth";

import PkiInputForm from "@/components/pflege-ki/PkiInputForm";
import PkiResultTab from "@/components/pflege-ki/PkiResultTab";
import PkiDraftsList from "@/components/pflege-ki/PkiDraftsList";
import PkiNbaTab from "@/components/pflege-ki/PkiNbaTab";
import PkiInterviewModus from "@/components/pflege-ki/PkiInterviewModus";
import PkiUsageBadge from "@/components/pflege-ki/PkiUsageBadge";
import PkiMdSimulator from "@/components/pflege-ki/PkiMdSimulator";
import PkiKundenakte from "@/components/pflege-ki/PkiKundenakte";
import PkiSammelAudit from "@/components/pflege-ki/PkiSammelAudit";
import PkiMonatslimitModal from "@/components/pflege-ki/PkiMonatslimitModal";
import PkiDashboard from "@/components/pflege-ki/PkiDashboard";
import PkiAmpel from "@/components/pflege-ki/PkiAmpel";
import PkiDokukette from "@/components/pflege-ki/PkiDokukette";
import PkiAufnahmegespraech from "@/components/pflege-ki/PkiAufnahmegespraech";
import PkiKlientenakte from "@/components/pflege-ki/PkiKlientenakte";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "interview", label: "Interview-Modus", kind: null },
  { id: "eingabe", label: "Eingabe", kind: null },
  { id: "ampel", label: "Pflegeampel", kind: null },
  { id: "dokukette", label: "Dokukette", kind: null },
  { id: "aufnahme", label: "Aufnahmegespräch", kind: null },
  { id: "klientenakte", label: "360°-Klientenakte", kind: null },
  { id: "sis", label: "SIS", kind: "sis" },
  { id: "massnahmen", label: "Maßnahmenplan", kind: "massnahmen" },
  { id: "pflegebericht", label: "Pflegebericht", kind: "pflegebericht" },
  { id: "kassenbrief", label: "Kassenbrief", kind: "kassenbrief" },
  { id: "hoehergruppe", label: "Höherstufung", kind: "hoehergruppe" },
  { id: "qualitaet", label: "Qualitätscheck", kind: "qualitaet" },
  { id: "nba", label: "NBA-Analyse", kind: null },
  { id: "md", label: "MD-Simulator", kind: null },
  { id: "kundenakte", label: "Kundenakte", kind: null },
  { id: "sammel", label: "Sammel-Audit", kind: null },
  { id: "entwuerfe", label: "Entwürfe", kind: null },
];

const EMPTY_INPUT = {
  kundendaten: {},
  pflegesituation: {},
  risiken: {},
  behandlungspflege: {},
  freitext: {},
  rohbericht: "",
};

const FORM_STORAGE_KEY = "dh-pki-form-v1";

function readSavedForm() {
  try {
    return JSON.parse(sessionStorage.getItem(FORM_STORAGE_KEY)) || null;
  } catch {
    return null;
  }
}

export default function CampusPflegeKi() {
  const { user } = useCampusAuth();
  const [access, setAccess] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [input, setInput] = useState(() => readSavedForm()?.input || EMPTY_INPUT);
  const [customerLabel, setCustomerLabel] = useState(() => readSavedForm()?.customerLabel || "");
  const [outputs, setOutputs] = useState({});  // {kind: {text, generated_at, draft_id}}
  const [busy, setBusy] = useState(null);      // kind currently generating
  const [usageKey, setUsageKey] = useState(0); // increment to force PkiUsageBadge reload
  const [monatslimitDetail, setMonatslimitDetail] = useState(null);  // strukturiertes 429-detail

  // Access-Check
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pflege-ki/access`, { withCredentials: true });
        setAccess(r.data);
      } catch (e) {
        setAccess({ ok: false, tier: user?.tier, error: e?.response?.data?.detail });
      }
    })();
  }, [user?.tier]);

  // Formular-Gedächtnis: Eingaben überleben Seiten-Reload (Session-Speicher,
  // wird beim Abmelden und bei "Eingabe leeren" gelöscht)
  useEffect(() => {
    try {
      sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify({ input, customerLabel }));
    } catch {}
  }, [input, customerLabel]);

  const resetInput = () => {
    setInput(EMPTY_INPUT);
    setCustomerLabel("");
    setOutputs({});
    try { sessionStorage.removeItem(FORM_STORAGE_KEY); } catch {}
    toast.message("Eingabe geleert.");
  };

  const generate = useCallback(async (kind, extraContext, variant = null) => {
    setBusy(kind);
    try {
      // 1) Async-Job starten — Cloudflare-Timeout-sicher
      const start = await axios.post(`${API}/pflege-ki/generate-async`, {
        kind, input, extra_context: extraContext || null, variant: variant || null,
      }, { withCredentials: true });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("Kein Job angelegt.");

      // 2) Polling bis status=done (max. 180 s, alle 2 s)
      const maxAttempts = 90;
      let done = null;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((res) => setTimeout(res, 2000));
        const status = await axios.get(`${API}/pflege-ki/generate-jobs/${jobId}`, { withCredentials: true });
        const job = status.data || {};
        if (job.status === "done") { done = job.result; break; }
        if (job.status === "error") {
          throw {
            response: {
              status: job.error_code || 500,
              data: { detail: job.error || "Fehler bei der Generierung." },
            },
          };
        }
      }
      if (!done) throw new Error("Zeitüberschreitung — der Job läuft noch. Bitte erneut versuchen.");

      setOutputs((prev) => ({
        ...prev,
        [kind]: {
          text: done.output_text,
          generated_at: done.generated_at,
          draft_id: null,
          halluzinationscheck: done.halluzinationscheck || null,
        },
      }));
      setUsageKey((k) => k + 1);

      // Auto-Entwurf: jede Ausgabe sofort persistent sichern (kein Datenverlust
      // bei Abmelden/Reload). Manuelles "Entwurf speichern" aktualisiert denselben Draft.
      try {
        const r = await axios.post(`${API}/pflege-ki/drafts`, {
          kind, input, output_text: done.output_text,
          customer_label: customerLabel || null, status: "entwurf",
          halluzinationscheck: done.halluzinationscheck || null,
        }, { withCredentials: true });
        setOutputs((prev) => ({
          ...prev,
          [kind]: { ...(prev[kind] || {}), draft_id: r.data.id },
        }));
        toast.success("KI-Entwurf erstellt & automatisch gespeichert.");
      } catch (autoErr) {
        toast.success("KI-Entwurf erstellt.");
        const d = autoErr?.response?.data?.detail;
        toast.warning(
          (d && typeof d === "object" && d.message) ||
          (typeof d === "string" ? d : "Automatisches Speichern fehlgeschlagen – bitte manuell „Entwurf speichern“ klicken.")
        );
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Strukturierter 429: Monatslimit (Single-Held) → eigenes Upgrade-Modal
      if (status === 429 && detail && typeof detail === "object" && detail.code === "MONTHLY_TIER_LIMIT") {
        setMonatslimitDetail(detail);
      } else if (status === 429) {
        toast.error(typeof detail === "string" ? detail : "Tageslimit erreicht.");
      } else {
        const msg = (typeof detail === "string" ? detail : null) || e?.message || "KI-Fehler. Bitte später erneut versuchen.";
        toast.error(msg);
      }
      setUsageKey((k) => k + 1);
    } finally {
      setBusy(null);
    }
  }, [input, customerLabel]);

  const saveDraft = useCallback(async (kind, text, status = "entwurf") => {
    try {
      const r = await axios.post(`${API}/pflege-ki/drafts`, {
        id: outputs[kind]?.draft_id || undefined,
        kind, input, output_text: text, customer_label: customerLabel || null, status,
        halluzinationscheck: outputs[kind]?.halluzinationscheck || null,
      }, { withCredentials: true });
      setOutputs((prev) => ({
        ...prev,
        [kind]: { ...(prev[kind] || {}), text, draft_id: r.data.id },
      }));
      toast.success(status === "freigegeben" ? "Freigegeben & gespeichert." : "Entwurf gespeichert.");
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    }
  }, [input, customerLabel, outputs]);

  // Gated content
  if (access === null) {
    return (
      <div data-testid="pki-loading" className="rounded-3xl border border-cream-300 bg-white p-8 text-sand-600">
        Lade Pflege-KI …
      </div>
    );
  }
  if (!access?.ok) {
    return <PkiLocked tier={access?.tier} required={access?.required_tiers || ["Single-Held"]} />;
  }

  return (
    <div className="space-y-6" data-testid="pki-page">
      {/* MONATSLIMIT-MODAL (Single-Held) */}
      <PkiMonatslimitModal
        open={!!monatslimitDetail}
        detail={monatslimitDetail}
        onClose={() => setMonatslimitDetail(null)}
      />

      {/* HEADER — nur bei Detail-Ansichten (Dashboard hat eigenen Hero) */}
      {activeTab !== "overview" && (
        <div className="space-y-2">
          <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-clay-dark">
            Dickies-Campus · 49 € Pro · KI-Assistenz
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-sand-800 leading-tight">
            Pflege-KI Assistenz
          </h1>
          <p className="text-sand-600 text-[14.5px] max-w-3xl leading-relaxed">
            Strukturierte Entwürfe für SIS, Maßnahmenpläne, Pflegeberichte, Kassenbriefe,
            Höherstufungen und Qualitätschecks. <strong>Alle Ausgaben sind Entwürfe</strong> –
            eine Pflegefachkraft muss prüfen, bearbeiten und freigeben.
          </p>
        </div>
      )}

      {/* DISCLAIMER + DSGVO-BANNER — nur bei Detail-Ansichten (Overview zeigt kompakt) */}
      {activeTab !== "overview" && (
        <div data-testid="pki-disclaimer"
          className="rounded-2xl border border-brand-darkred/40 bg-peach-50 p-4 text-[13px] leading-relaxed text-sand-800">
          <div className="font-bold text-brand-darkred mb-1">
            KI-Entwurf · fachliche Freigabe erforderlich
          </div>
          {access.disclaimer}<br />
          <span className="text-sand-600 text-[12.5px]">
            <strong>Datenschutz-Prototyp:</strong> Im aktuellen Prototyp ausschließlich Testdaten oder
            klar anonymisierte Eingaben verwenden. Echte Patientendaten erst nach AV-Vertrag mit Anthropic.
          </span>
        </div>
      )}

      {/* USAGE-BADGE · Tageslimit pro Modul */}
      <PkiUsageBadge refreshKey={usageKey} />

      {/* Aktiver Kunde (falls gewählt) */}
      {customerLabel && (
        <div data-testid="pki-active-customer" className="flex items-center justify-end">
          <span className="inline-flex items-center gap-2 rounded-full bg-sand-800 text-cream-50 px-3 h-8 text-[12px]">
            Aktiv: {customerLabel}
            <button onClick={resetInput} data-testid="pki-reset" className="opacity-70 hover:opacity-100">✕</button>
          </span>
        </div>
      )}

      {/* Zurück-Nav (nur wenn NICHT auf Overview) */}
      {activeTab !== "overview" && (
        <div className="flex items-center justify-between gap-3 sticky top-2 z-20">
          <button
            type="button"
            onClick={() => setActiveTab("overview")}
            data-testid="pki-back-to-overview"
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-cream-300 hover:border-brand-darkred/40 hover:bg-brand-rose/40 px-4 h-11 text-[13.5px] font-semibold text-sand-800 shadow-soft transition-colors"
          >
            <ArrowRight className="h-3.5 w-3.5 rotate-180" strokeWidth={2.2} />
            Übersicht
          </button>
          <span className="text-[12.5px] text-sand-600 font-medium truncate">
            {TABS.find((t) => t.id === activeTab)?.label}
          </span>
        </div>
      )}

      {/* CONTENT */}
      <div data-testid="pki-content" className="min-h-[400px]">
        {activeTab === "overview" && (
          <PkiDashboard
            userLabel={user?.name || user?.email?.split("@")[0] || ""}
            onOpenTab={setActiveTab}
          />
        )}        {activeTab === "interview" && (
          <PkiInterviewModus onUsageChange={() => setUsageKey((k) => k + 1)} />
        )}
        {activeTab === "ampel" && (
          <PkiAmpel />
        )}
        {activeTab === "dokukette" && (
          <PkiDokukette />
        )}
        {activeTab === "aufnahme" && (
          <PkiAufnahmegespraech />
        )}
        {activeTab === "klientenakte" && (
          <PkiKlientenakte />
        )}
        {activeTab === "eingabe" && (
          <PkiInputForm input={input} setInput={setInput}
            customerLabel={customerLabel} setCustomerLabel={setCustomerLabel} />
        )}
        {activeTab === "entwuerfe" && (
          <PkiDraftsList
            onLoad={(d) => {
              setInput(d.input_snapshot || EMPTY_INPUT);
              setCustomerLabel(d.customer_label || "");
              setOutputs((prev) => ({
                ...prev,
                [d.kind]: { text: d.output_text, draft_id: d.id, generated_at: d.updated_at },
              }));
              const tab = TABS.find((t) => t.kind === d.kind);
              setActiveTab(tab?.id || "eingabe");
            }}
          />
        )}
        {activeTab === "nba" && (
          <PkiNbaTab
            customerLabel={customerLabel}
            kundenInput={input}
            initialSis={outputs.sis?.text || ""}
            initialPlan={outputs.massnahmen?.text || ""}
          />
        )}
        {activeTab === "md" && (
          <PkiMdSimulator
            initialSis={outputs.sis?.text || ""}
            initialPlan={outputs.massnahmen?.text || ""}
            initialKassenbrief={outputs.kassenbrief?.text || ""}
            initialHoeherstufung={outputs.hoehergruppe?.text || ""}
            onUsageChange={() => setUsageKey((k) => k + 1)}
          />
        )}
        {activeTab === "kundenakte" && (
          <PkiKundenakte />
        )}
        {activeTab === "sammel" && (
          <PkiSammelAudit />
        )}
        {TABS.filter((t) => t.kind).map((t) => (
          activeTab === t.id && (
            <PkiResultTab key={t.id}
              tabId={t.id}
              kind={t.kind}
              label={t.label}
              output={outputs[t.kind]}
              onGenerate={generate}
              onSave={saveDraft}
              busy={busy === t.kind}
              customerLabel={customerLabel}
              hasInput={Object.values(input).some((v) =>
                typeof v === "string" ? v.length > 0 : Object.values(v || {}).some(Boolean)
              )}
              rohbericht={input.rohbericht}
              setRohbericht={(v) => setInput((p) => ({ ...p, rohbericht: v }))}
              sisText={outputs.sis?.text || ""}
              planText={outputs.massnahmen?.text || ""}
            />
          )
        ))}
      </div>
    </div>
  );
}

function PkiLocked({ tier, required }) {
  const FEATURES = [
    { title: "SIS-Entwurf in 20 Sekunden", desc: "Strukturierte Informationssammlung (A1–A6) auf Knopfdruck – passt zur Pflegesituation, nennt offene Punkte, keine Erfindungen." },
    { title: "Maßnahmenplan ohne Schreibarbeit", desc: "7-Spalten-Tabelle (Problem · Ziel · Maßnahme · Häufigkeit · Verantwortung · Beobachtung · Evaluation). Konsistent zur SIS." },
    { title: "Pflegebericht per Diktat", desc: "30 Sek. Stichpunkte ins Handy sprechen → Whisper transkribiert → KI macht den sachlichen Bericht. Spart 5–15 Min/Tag." },
    { title: "Kassenbriefe & Höherstufung", desc: "Formgerechte Briefe an Pflege-/Krankenkasse + Widerspruchs- und Höherstufungs-Entwürfe nach NBA-Logik." },
    { title: "Qualitätscheck mit KI", desc: "Lässt SIS, Maßnahmenplan & Kassenbrief gegenchecken: fehlende Ziele, Widersprüche, unklare Formulierungen." },
    { title: "PDF-Export & Freigabe-Workflow", desc: "Jeder Entwurf bekommt Status (Entwurf / In Prüfung / Freigegeben) und kann als sauberes PDF exportiert werden." },
  ];
  const requiredLabel = (required || []).filter((t) => t !== "Admin").join(" / ");

  return (
    <div data-testid="pki-locked" className="space-y-8">
      {/* HERO */}
      <div className="rounded-3xl border border-terra/30 bg-gradient-to-br from-peach-50 via-cream-50 to-white p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-terra/12 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-terra/15 text-terra px-3 h-7 text-[10.5px] uppercase tracking-[0.22em] font-bold mb-4">
            <Lock className="h-3.5 w-3.5" strokeWidth={2} /> Tier-gesperrt
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-sand-800 leading-tight">
            Pflege-KI · spart 30&nbsp;Minuten pro <span className="text-terra">Pflegekraft &amp; Tag</span>.
          </h1>
          <p className="mt-3 text-sand-700 text-[14.5px] leading-relaxed max-w-3xl">
            Die KI-Assistenz für SIS, Maßnahmenpläne, Pflegeberichte, Kassenbriefe, Höherstufungen
            und Qualitätschecks ist Mitgliedern ab <strong>{requiredLabel}</strong> vorbehalten.
            Du bist aktuell im Tier <strong>{tier || "Helden-Basic"}</strong>.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
            <Link to="/campus/pro" data-testid="pki-upgrade-cta">
              <Button className="rounded-full h-12 px-7 bg-terra hover:bg-terra-dark text-white text-[14px] font-semibold">
                Auf Single-Held upgraden · 49 €/Monat
                <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
              </Button>
            </Link>
            <Link to="/campus/team" data-testid="pki-upgrade-cta-team"
              className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-darkred underline-offset-4 hover:underline">
              Für Teams: Hauptquartier ansehen <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* WHAT YOU GET */}
      <div data-testid="pki-features-preview" className="rounded-3xl border border-cream-300 bg-white p-7">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-5">
          Was du im Single-Held-Tier bekommst
        </div>
        <ul className="grid md:grid-cols-2 gap-3">
          {FEATURES.map((f, i) => (
            <li key={i} className="flex items-start gap-3 rounded-2xl bg-cream-50 border border-cream-300 p-4">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sand-800 text-cream-50 text-[12px] font-bold">
                {i + 1}
              </span>
              <div className="min-w-0">
                <div className="text-[14px] font-semibold text-sand-800">{f.title}</div>
                <div className="text-[12.5px] text-sand-600 leading-snug mt-0.5">{f.desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* EXAMPLE OUTPUT TEASER */}
      <div className="rounded-3xl border border-cream-300 bg-cream-50 p-7">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-3">
          Beispiel · so sieht ein KI-Pflegebericht aus
        </div>
        <div className="rounded-2xl bg-white border border-cream-300 p-5">
          <div className="text-[12px] text-sand-500 mb-2 font-mono">Stichpunkte vor KI:</div>
          <div className="text-[13.5px] text-sand-700 italic mb-4">
            „Kunde heute müde, BZ 178, Medikamente genommen, keine Schmerzen."
          </div>
          <div className="text-[12px] text-sand-500 mb-2 font-mono">→ Pflegebericht nach KI:</div>
          <div className="text-[13.5px] text-sand-800 leading-relaxed">
            <strong>Pflegebericht-Eintrag · 15.06.2026, 09:15 Uhr</strong><br />
            Der Kunde zeigte sich heute müde, war jedoch wach und ansprechbar.
            Der Blutzuckerwert betrug 178&nbsp;mg/dl. Die Medikamente wurden wie verordnet eingenommen.
            Schmerzen wurden vom Kunden verneint. Keine Auffälligkeiten bei Vitalzeichen-Kontrolle.
          </div>
        </div>
        <p className="mt-3 text-[12px] text-sand-500">
          Echte KI-Ausgabe von Claude Sonnet 4.5. Pro Pflegekraft im Schnitt 6&nbsp;Berichte/Tag × 5&nbsp;Min = 30&nbsp;Min Zeitersparnis täglich.
        </p>
      </div>

      {/* SECONDARY CTA */}
      <div className="text-center pt-4">
        <Link to="/campus/pro" data-testid="pki-upgrade-cta-bottom">
          <Button className="rounded-full h-12 px-7 bg-sand-800 hover:bg-sand-700 text-cream-50 text-[14px] font-semibold">
            Jetzt freischalten · 49 €/Monat (jederzeit kündbar)
            <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
          </Button>
        </Link>
      </div>
    </div>
  );
}
